import { RemoteTrackPublication, Room } from 'twilio-video';
import { useLayoutEffect, useState } from 'react';
import { CallStatus } from '@hooks/useCallStatus';
import { CallManager } from '@helpers/CallManager';

function hasRemoteAV(room?: Room) {
  if (!room || room.participants.size === 0) return false;

  const remote = room.participants.values().next().value!;

  return remote.audioTracks.size > 0 || remote.videoTracks.size > 0;
}

// Returns 'in-progress' when the remote participant has started A/V, otherwise 'not-started'
// NB: never returns 'completed' or 'in-progress'.
function useTestPatientCallStatus(room?: Room, callEnded = false): CallStatus {
  const [status, setStatus] = useState<CallStatus>(() => (hasRemoteAV(room) ? 'in-progress' : 'not-started'));

  // when the remote participant starts A/V, set the call status to in-progress.
  // NB - this is a layout effect because I don't want to miss any events between the creation of the "status"
  // state variable and the subscription to the room's "trackPublished" event.
  useLayoutEffect(() => {
    function onTrackPublished(track: RemoteTrackPublication) {
      if (track.kind === 'audio' || track.kind === 'video') {
        setStatus('in-progress');
      }
    }

    if (!room) return () => {};

    const localDataTrack = room.localParticipant?.dataTracks?.values().next().value?.track;

    let manager: CallManager | undefined;

    if (localDataTrack) {
      manager = new CallManager(room, localDataTrack, {
        onCallAccepted: (senderId) => {
          console.log('Clinician received callAccepted from patient', senderId);
          setStatus('in-progress');
        },
        onCallCancelled: () => {},
        onCallDeclined: () => {},
        onCallOffer: () => {}
      });
    } else {
      console.warn('Local DataTrack not available. CallManager was not initialized.');
    }

    room.on('trackPublished', onTrackPublished);
    return () => {
      room.off('trackPublished', onTrackPublished);
      manager?.disconnect();
    };
  }, [room]);

  return callEnded ? 'completed' : status;
}

export default useTestPatientCallStatus;
